import { useState } from "react";
import { Link } from "react-router";
import { Trans } from "@lingui/macro";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@app/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@app/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@app/components/ui/alert-dialog";
import { ExperimentStatusBadge } from "@app/components/ExperimentStatusBadge";
import { Progress } from "@app/components/ui/progress";
import { fmtMs } from "@app/lib/format";
import { Clock, Zap, BarChart3, MoreVertical, Trash2 } from "lucide-react";

interface Experiment {
  id: string;
  name: string;
  description: string | null;
  status: string;
  total_runs: number;
  completed_runs: number;
  failed_runs: number;
  avg_latency_ms: number | null;
  avg_score: number | null;
  /** Which surface this measurement scored. Anything but PROJECTION is the retired flat
   *  aggregation, rows predating the column included. */
  scored_surface?: string | null;
  created_at: string;
}

interface ExperimentCardProps {
  experiment: Experiment;
  datasetId: string;
  onDelete?: (experimentId: string) => void;
}

export function ExperimentCard({ experiment, datasetId, onDelete }: ExperimentCardProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const progress =
    experiment.total_runs > 0
      ? ((experiment.completed_runs + experiment.failed_runs) /
          experiment.total_runs) *
        100
      : 0;

  return (
    <>
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer l'expérience</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer <strong>{experiment.name}</strong> ? Cette action supprimera également tous les runs et évaluations associés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              onClick={() => onDelete?.(experiment.id)}
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="relative">
        {onDelete && (
          <div className="absolute top-2 right-2 z-10" onClick={(e) => e.preventDefault()}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors">
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  className="text-red-600 focus:text-red-600 cursor-pointer"
                  onClick={() => setConfirmOpen(true)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Supprimer
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        <Link to={`/datasets/${datasetId}/experiments/${experiment.id}`}>
          <Card className="hover:shadow-lg hover:border-primary/50 transition-all duration-200 cursor-pointer">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg pr-6">{experiment.name}</CardTitle>
                <div className="flex shrink-0 items-center gap-2">
                  {/* A historical flat measurement is named on the card: its score does not compare
                      with a projection's, and it can no longer be run. */}
                  {experiment.scored_surface !== "PROJECTION" && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                      <Trans>Agrégation à plat (retirée)</Trans>
                    </span>
                  )}
                  <ExperimentStatusBadge status={experiment.status} />
                </div>
              </div>
              {experiment.description && (
                <p className="text-sm text-slate-500 line-clamp-2">
                  {experiment.description}
                </p>
              )}
            </CardHeader>
            <CardContent>
              {experiment.status === "RUNNING" && (
                <div className="mb-3">
                  <Progress value={progress} className="h-2" />
                  <p className="text-xs text-slate-500 mt-1">
                    {experiment.completed_runs + experiment.failed_runs}/
                    {experiment.total_runs} runs
                  </p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-2 text-sm">
                <div className="flex items-center gap-1 text-slate-600">
                  <Zap className="h-3.5 w-3.5" />
                  <span>{experiment.total_runs} runs</span>
                </div>
                <div className="flex items-center gap-1 text-slate-600">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{fmtMs(experiment.avg_latency_ms)}</span>
                </div>
                <div className="flex items-center gap-1 text-slate-600">
                  <BarChart3 className="h-3.5 w-3.5" />
                  <span>
                    {experiment.avg_score !== null
                      ? `${(experiment.avg_score * 100).toFixed(0)}%`
                      : "—"}
                  </span>
                </div>
              </div>

              <p className="text-xs text-slate-400 mt-3">
                {new Date(experiment.created_at).toLocaleDateString("fr-FR", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}
              </p>
            </CardContent>
          </Card>
        </Link>
      </div>
    </>
  );
}
